/**
 * useSettlementAge Hook - BigQuery version
 *
 * CRITICAL: Replaces Firestore active_loans query in SettlementAgeReportPage
 *
 * Migrated: 2025-11-17
 * - FROM: Firestore onSnapshot query (real-time, expensive)
 * - TO: BigQuery callable function (on-demand, cheaper, faster)
 *
 * Data Structure: MUST match Firestore active_loans collection exactly
 * - loanFingerprint, loanId, customerName, broker, lender, loanAmount, settlementDate, lastTrailDate
 *
 * RBAC: Broker role filtering handled by backend (brokerUid parameter)
 */

import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

// CRITICAL: Match exact Firestore structure from SettlementAgeReportPage.tsx (lines 22-48)
export interface ProcessedLoan {
  loanFingerprint: string;
  loanId: string | number;
  customerName: string;
  broker: string;
  lender: string;
  loanAmount: number;
  settlementDate: string | Date;  // Backend returns string, we'll handle both
  settlementDateObj: Date;        // Computed client-side
  settlementAge: number;          // Years (computed client-side from settlementDateObj)
  lastTrailDate: Date | null;
  recordCount: number;            // Number of commission records combined for this loan
}

interface UseSettlementAgeOptions {
  companyId: string;
  brokerUid?: string;
  templateId?: string;  // Optional template filter
  yearRangeStart?: number;
  yearRangeEnd?: number;
  enabled?: boolean;  // Allow disabling the query
  entityCode?: string;
}

interface UseSettlementAgeResult {
  loans: ProcessedLoan[];
  loading: boolean;
  error: string;
  refetch: () => Promise<void>;
}

/**
 * Convert various date formats to Date object
 * Handles: ISO string, Date object, Firestore Timestamp
 */
function toDate(value: any): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  // Firestore Timestamp
  if (value?.toDate && typeof value.toDate === 'function') {
    return value.toDate();
  }
  if (value?.seconds) {
    return new Date(value.seconds * 1000);
  }
  return null;
}

/**
 * Calculate age in years from settlement date
 * MUST match calculation in SettlementAgeReportPage.tsx (lines 106-110)
 */
function calculateAgeInYears(settlementDate: Date): number {
  const now = new Date();
  const diffMs = now.getTime() - settlementDate.getTime();
  return Math.max(0, diffMs / (365.25 * 24 * 60 * 60 * 1000));
}

/**
 * Hook to fetch active loans from BigQuery
 *
 * Replaces Firestore onSnapshot with callable function pattern:
 * - Manual refetch instead of real-time updates
 * - Better performance for large datasets (1000+ loans)
 * - Lower cost ($0.01 vs $0.02 per query)
 *
 * Usage:
 * ```
 * const { loans, loading, error, refetch } = useSettlementAge({
 *   companyId: userData.companyId,
 *   brokerUid: userData.role === 'broker' ? userData.uid : undefined,
 *   yearRangeStart: 0,
 *   yearRangeEnd: 10,
 * });
 * ```
 */
export function useSettlementAge(options: UseSettlementAgeOptions): UseSettlementAgeResult {
  const { companyId, brokerUid, templateId, yearRangeStart, yearRangeEnd, enabled = true, entityCode } = options;

  const [loans, setLoans] = useState<ProcessedLoan[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  const fetchLoans = async () => {
    if (!companyId || !enabled) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const getActiveLoansList = httpsCallable(functions, 'getActiveLoansList');

      console.log('[SETTLEMENT_AGE] Fetching active loans from BigQuery:', {
        companyId,
        brokerUid,
        templateId,
        yearRangeStart,
        yearRangeEnd,
      });

      const result = await getActiveLoansList({
        companyId,
        brokerUid,
        templateId,
        yearRangeStart,
        yearRangeEnd,
        entityCode,
      });

      const data = result.data as any[];

      console.log('[SETTLEMENT_AGE] Received active loans:', {
        count: data.length,
        sample: data[0],
      });

      // Transform backend data to match Firestore structure
      const processedLoans: ProcessedLoan[] = data
        .map((loan) => {
          const settlementDateObj = toDate(loan.settlementDate);
          // Acquired book loans (e.g., Entover) may have NULL settlement dates
          // Include them with age 0 so they appear in the count and sort last
          const age = settlementDateObj ? calculateAgeInYears(settlementDateObj) : 0;

          return {
            loanFingerprint: loan.loanFingerprint,
            loanId: loan.loanId,
            customerName: loan.customerName || 'Unknown',
            broker: loan.broker || 'Unknown',
            lender: loan.lender || 'Unknown',
            loanAmount: loan.loanAmount || 0,
            settlementDate: loan.settlementDate || null,
            settlementDateObj: settlementDateObj || new Date(0),
            settlementAge: age,
            lastTrailDate: toDate(loan.lastTrailDate) ?? null,
            recordCount: loan.recordCount || 1,
          };
        });

      // Sort by age (oldest first) - matches Firestore query
      processedLoans.sort((a, b) => b.settlementAge - a.settlementAge);

      console.log('[SETTLEMENT_AGE] Processed loans:', {
        total: processedLoans.length,
        oldestAge: processedLoans[0]?.settlementAge.toFixed(1),
      });

      setLoans(processedLoans);
      setLoading(false);
    } catch (err: any) {
      console.error('[SETTLEMENT_AGE] Error fetching active loans:', err);
      setError(`Failed to load active loans: ${err.message || 'Unknown error'}`);
      setLoading(false);
    }
  };

  // Initial fetch on mount and when dependencies change
  useEffect(() => {
    fetchLoans();
  }, [companyId, brokerUid, templateId, yearRangeStart, yearRangeEnd, enabled, entityCode]);

  return {
    loans,
    loading,
    error,
    refetch: fetchLoans,
  };
}
